import { i18n } from './i18n';

/** An option whose label (and description) may name a translation key. */
type TranslatableOption = {
  label: string;
  labelKey?: string;
  description?: string;
  descriptionKey?: string;
};

/**
 * `i18n.t` for a key that is data rather than a literal — an option's `labelKey`.
 *
 * The typed `t` only takes keys it can see in the code, and these live in `mdc-constants`. They are
 * checked instead by a test there, which asks this instance whether every one exists in English.
 * The English label rides along as the fallback, so a key that went missing shows English, not a key.
 */
const translateKey = i18n.t as unknown as (key: string, options: { defaultValue: string }) => string;

/** The option with its label and description in the language the UI is in now. */
export const translateOption = <T extends TranslatableOption>(option: T): T => ({
  ...option,
  label: option.labelKey ? translateKey(option.labelKey, { defaultValue: option.label }) : option.label,
  ...(option.description !== undefined && option.descriptionKey
    ? { description: translateKey(option.descriptionKey, { defaultValue: option.description }) }
    : {}),
});

/**
 * A list of options, translated — for a select, a picker or a label lookup.
 *
 * Call it while rendering, never when a module loads: a list translated at import time is fixed in
 * whatever language was active then, which before `initI18n` is none at all.
 */
export const translateOptions = <T extends TranslatableOption>(options: readonly T[]): T[] =>
  options.map(translateOption);

/**
 * The label for a stored option value, in the language the UI is in now — for a value read back
 * from a record rather than picked from a list, such as an engine's `type`. A value no option
 * names is shown as it is, so a record holding something the list has since dropped still reads.
 */
export const optionValueLabel = <T extends TranslatableOption & { value: unknown }>(
  options: readonly T[],
  value: T['value'] | null | undefined,
): string | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const option = options.find((candidate) => candidate.value === value);

  return option ? translateOption(option).label : String(value);
};
